import { getElevation } from '@m1/geo-utils';
import OlMap from '@m1/ol-map';
import useUpdateEffect from '@restart/hooks/useUpdateEffect';
import classNames from 'classnames/bind';
import { toLonLat } from 'ol/proj';
import React, { useState } from 'react';
import { RiFlightLandLine } from 'react-icons/ri';
import { useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import styles from './common.module.scss';

import actions from '@/actions';
import ConfirmSlider from '@/components/ui/ConfirmSlider';
import Select from '@/components/ui/kit/Select';
import LabelGroup from '@/components/ui/LabelGroup';
import { MODE } from '@/define/robotStatus';
import useActionCommand from '@/hooks/command/mavlink/useActionCommand';
import EventEmitter from '@/libs/EventEmitter';
import { NotifierService as notifier } from '@/libs/Notifier';
import { executeMavLinkCommand } from '@/utils/CommandUtils';

const cx = classNames.bind(styles);

const Panel = ({ data: robots }) => {
  const dispatch = useDispatch();
  const [isImmediately, setIsImmediately] = useState(true);
  const [isProcessing, setProcessing] = useState(false);
  const [targetLocation, setTargetLocation] = useState(null);
  const actionCommand = useActionCommand();
  const map = OlMap.getMap();

  useUpdateEffect(() => {
    if (!isImmediately) {
      map.getTargetElement().style.cursor = 'crosshair';
      map.on('click', clickOnMap);
    }

    return () => {
      map.getTargetElement().style.cursor = 'default';
      map.un('click', clickOnMap);
    };
  }, [robots, isImmediately]);

  //#region Process Functions
  const process = async (robot) => {
    try {
      // 즉시 착륙
      if (isImmediately) {
        await executeMavLinkCommand(robot, actionCommand.setMode(MODE.ARDUPILOT.PLANE.QLAND.id));
      }
      // 홈 설정 후 QRTL
      else {
        const { lat, lng, elevation } = targetLocation;

        await executeMavLinkCommand(robot, actionCommand.setHome(lat, lng, elevation));
        await executeMavLinkCommand(robot, actionCommand.setMode(MODE.ARDUPILOT.PLANE.QRTL.id));
      }

      onSuccess(robot);
    } catch (error) {
      onFailure(robot);
    }
  };
  //#endregion

  //#region Result Handlers
  const onSuccess = (robot) => {
    EventEmitter.publish(`${robot.id}/event/removeGoto`);

    let message = '';
    if (isImmediately) {
      message = `${robot.name} is landing.`;
    } else {
      message = `${robot.name} is returning to home and landing.`;
    }

    notifier.success(message);
    setProcessing(false);
  };

  const onFailure = (robot) => {
    notifier.error(`Failed to land on ${robot.name}.`);
    setProcessing(false);
  };
  //#endregion

  useUpdateEffect(() => {
    if (!isProcessing) {
      dispatch(actions.command.resetActivePanels());
      return;
    }

    process(robots[0]);
  }, [isProcessing]);

  const handleChange = (e) => {
    setIsImmediately(e.target.value);
  };

  const clickOnMap = (e) => {
    const [lng, lat] = toLonLat(e.coordinate);

    getElevation({ lat, lng }).then((elevation) => {
      setTargetLocation({ lat, lng, elevation });
      setProcessing(true);
    });
  };

  const doCommand = () => {
    // 처리 중 상태로 변경
    setProcessing(true);
  };

  return (
    <div className={cx('container')}>
      <div className={cx('form')}>
        <LabelGroup htmlFor="action" label="Action">
          <Select name="action" value={isImmediately} onChange={handleChange}>
            <option value={true}>Immediately</option>
            <option value={false}>Set Home & RTH</option>
          </Select>
        </LabelGroup>
      </div>
      {isImmediately && (
        <ConfirmSlider
          width="100%"
          height={40}
          onConfirm={doCommand}
          isProcessing={isProcessing}
          labelStyle={{ fontSize: 12 }}
        />
      )}
      {!isImmediately && <div className={cx('desc')}>Click on a location on the map where you want to land</div>}
    </div>
  );
};

const Command = {};
Command.id = uuidv4();
Command.name = 'QLand';
Command.displayMode = 'sidebar';
Command.singleRobotOnly = true;
Command.commanderOnly = true;
Command.Icon = RiFlightLandLine;
Command.Panel = Panel;

export default Command;
